'use client';

import { useEffect, useState } from 'react';
import { useInputsValidation } from '@brahma/hooks/use-inputs-validation';
import { isEmptyString, validateEmail } from '@brahma/utils';
import useLocale from '$hooks/use-locale';
import { ONBOARDING } from '$locale/constants';
import { useMutation, AnalyticsManager } from '@brahma/hooks';
import { API_URLS } from '$constants/apis';
import type { LoginBodyParameter, IBFFResponse, User } from '@dfg/brahma-types';
import { POST } from '@brahma/utils';
import useGlobalContext from '$hooks/use-global-context';
import { ContextAction } from '$context/global-context/types';
import { useLoadingRouter } from '@brahma/hooks';
import { PAGE_URLS } from '$constants/index';
import { addToast } from '@brahma/components';
import { TOAST_ERROR_TIME } from '$constants/common';
export const useLogin = () => {
  const {
    t
  } = useLocale(ONBOARDING);
  const [showBadge, setShowBadge] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const globalContext = useGlobalContext();
  const {
    routerPush,
    loading: routerLoading
  } = useLoadingRouter();
  const {
    values,
    errors,
    handleChange,
    handleSubmit
  } = useInputsValidation({
    email: '',
    password: ''
  }, {
    password: [(value: string) => isEmptyString(value) ? t('login.error2') : ''],
    email: [validateEmail(t('login.error1'))]
  });
  const {
    data: loginData,
    mutateAsync,
    loading,
    error: loginError
  } = useMutation<IBFFResponse<User>, LoginBodyParameter>({
    url: API_URLS.logIn,
    options: {
      method: POST
    }
  });
  const callMutateAsync = () => {
    mutateAsync({
      email: values.email as string,
      password: values.password as string
    });
  };
  const onSubmit = () => {
    setShowBadge(false);
    setSubmitClicked(true);
    handleSubmit(callMutateAsync);
  };
  const handleForgotPassword = () => {
    routerPush(PAGE_URLS.forgotPassword);
  };
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  useEffect(() => {
    if (loginData?.data && loginData.code === 200) {
      setShowBadge(true);
      globalContext.dispatch({
        type: ContextAction.setUser,
        payload: {
          ...loginData.data,
          userGroups: loginData.data.userGroups ?? []
        }
      });
      AnalyticsManager.getInstance().updateUserData(loginData.data);
      if (!loginData.data.firstName) {
        routerPush(PAGE_URLS.aboutYourself);
      } else {
        routerPush(PAGE_URLS.siteVerificationDashboard);
      }
    }
  }, [loginData]);
  useEffect(() => {
    if (loginError?.error?.dfgMessage) {
      addToast({
        content: loginError.error.dfgMessage,
        variant: 'error',
        autoCloseDuration: TOAST_ERROR_TIME
      });
    }
  }, [loginError]);
  return {
    t,
    showBadge,
    showPassword,
    submitClicked,
    values,
    errors,
    loading,
    routerLoading,
    loginData,
    handleChange,
    onSubmit,
    handleForgotPassword,
    togglePasswordVisibility
  };
};